// ---------------------------------------------------------------------------
// DOM-018 Phase 5 — Domain Verification Polling CQRS Event Catalog
// ---------------------------------------------------------------------------
// Story DOM-018: "Domain verification polling updates status and emits
// user-visible progress/failure reasons."
//
// Phase 1 (LANDED) shipped the pure state-machine
// `packages/db/src/services/domain-verification-state.ts` with closed
// `DOMAIN_VERIFICATION_REASONS` (8) + verification-state columns on
// `page-builder-domains` (`pending`/`verifying`/`active`/`failed`).
//
// This file is the Phase 5 narrow follow-up: it catalogues the single
// `domain.verification_state_changed@v1` event the future polling worker
// (`workers/domain-verifier/`) will emit, so downstream materializers/DLQ
// consumers/UI badge listeners read it without taking a runtime dependency
// on the polling worker module.
//
// What this file is NOT:
//  - It does not emit events — that's the future polling worker (cron).
//  - It does not change Phase 1 state-machine or schema (additive only).
//  - It does not implement DoH lookups (`verifyDomainOwnership`).
//
// What it IS:
//  - The single source of truth for the `domain.verification_state_changed`
//    event string.
//  - Typed payload contract so the emitter + DLQ consumer cannot drift.
//  - Runtime narrowing guards so DLQ/audit-runner reject unknown event types
//    or unknown failure reasons.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of the 1 DOM-018 CQRS event type. Versioned with the
 * `@v1` suffix per platform convention (Governance Rule #7).
 *
 * One business fact per event (Governance Rule #6):
 *  - `verification_state_changed` — the polling worker landed a transition
 *    on the verification state-machine for one custom domain.
 */
export const DOM_018_DOMAIN_VERIFICATION_EVENT_TYPES = [
  "domain.verification_state_changed@v1",
] as const;

export type Dom018DomainVerificationEventType =
  (typeof DOM_018_DOMAIN_VERIFICATION_EVENT_TYPES)[number];

/**
 * Closed catalog of verification states — mirrors Phase 1 schema enum
 * (`pending`/`verifying`/`active`/`failed`).
 */
export const DOM_018_VERIFICATION_STATE_VOCAB = [
  "pending",
  "verifying",
  "active",
  "failed",
] as const;

export type Dom018VerificationStateVocab =
  (typeof DOM_018_VERIFICATION_STATE_VOCAB)[number];

/**
 * Closed catalog of failure reasons — mirrors Phase 1
 * `DOMAIN_VERIFICATION_REASONS` verbatim. Carried on the event when
 * `nextState` is `failed` so the Phase 6 UI badge renders the user-visible
 * reason without a runtime import from `@ease/db/services`.
 */
export const DOM_018_VERIFICATION_FAILURE_REASON_VOCAB = [
  "NS_NOT_FOUND",
  "NS_MISMATCH",
  "TXT_NOT_FOUND",
  "TXT_MISMATCH",
  "CNAME_MISMATCH",
  "TIMEOUT",
  "RATE_LIMITED",
  "PROVIDER_ERROR",
] as const;

export type Dom018VerificationFailureReasonVocab =
  (typeof DOM_018_VERIFICATION_FAILURE_REASON_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                    */
/* -------------------------------------------------------------------------- */

export function isDom018DomainVerificationEventType(
  value: unknown,
): value is Dom018DomainVerificationEventType {
  return (
    typeof value === "string" &&
    (DOM_018_DOMAIN_VERIFICATION_EVENT_TYPES as readonly string[]).includes(
      value,
    )
  );
}

export function isDom018VerificationStateVocab(
  value: unknown,
): value is Dom018VerificationStateVocab {
  return (
    typeof value === "string" &&
    (DOM_018_VERIFICATION_STATE_VOCAB as readonly string[]).includes(value)
  );
}

export function isDom018VerificationFailureReasonVocab(
  value: unknown,
): value is Dom018VerificationFailureReasonVocab {
  return (
    typeof value === "string" &&
    (
      DOM_018_VERIFICATION_FAILURE_REASON_VOCAB as readonly string[]
    ).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contract                                                  */
/* -------------------------------------------------------------------------- */

export interface Dom018VerificationStateChangedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK into Phase 1 `custom_domains` row. */
  customDomainId: string;
  /** Apex/subdomain string the polling worker tested. */
  domain: string;
  /** Previous state from the Phase 1 state-machine envelope. */
  prevState: Dom018VerificationStateVocab;
  /** New state from the Phase 1 state-machine envelope. */
  nextState: Dom018VerificationStateVocab;
  /**
   * Failure reason when `nextState` is `failed`, else null. Pins the
   * Phase 1 reason vocab — DLQ + UI badge consume this enum without
   * importing the state-machine module.
   */
  failureReason: Dom018VerificationFailureReasonVocab | null;
  /**
   * Monotonic attempt counter from the Phase 1 envelope. The polling worker
   * caps at `VERIFICATION_ATTEMPT_CAP = 30` before forcing a `TIMEOUT`
   * failure.
   */
  attempts: number;
  /**
   * ISO-8601 deadline carried from the Phase 1 envelope, present when the
   * domain is in `verifying`. Null on terminal transitions.
   */
  deadlineAt: string | null;
  /** ISO-8601 — when the polling worker observed the transition. */
  changedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (polling worker emitter, materializer, DLQ, audit-runner) `switch
 * (event.type)` and pull the right payload without any-casting.
 */
export type Dom018DomainVerificationEvent = {
  type: "domain.verification_state_changed@v1";
  payload: Dom018VerificationStateChangedPayload;
};
